import React, { useContext, useEffect, useState } from 'react';

import { Place } from '../../app/models/Place';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';

import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';

import { toast } from 'react-toastify';

import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useVehicleModelService from '../../app/services/hooks/useVehicleModelService';
import { VehicleModelDataForm, VehicleModelFilters } from '../../app/models/VehicleModel';
import VehicleModelDataTable from './components/tables/VehicleModelDataTable';
import VehicleModelCreate from './components/VehicleModelCreate';
import VehicleModelEdit from './components/VehicleModelEdit';
import { useHistory } from 'react-router-dom';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Modelos de vehículo',
        url: '/veicle-models',
        isActive: true
    }
];

const VehicleModels = () => {
    const history = useHistory();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { fetchingGetVehicleModels, getVehicleModels, deleteVehicleModel, activeVehicleModel } =
        useVehicleModelService();
    const [vehicleModels, setVehicleModels] = useState<VehicleModelDataForm[]>([]);
    const [vehicleModelIdEdit, setVehicleModelIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const { navigationPage } = useNavigationPage();

    const [vehicleModelFilter, setVehicleModelFilter] = useState<VehicleModelFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        reloadTable();
    }, [vehicleModelFilter]);

    const getAllVehicleModels = () => {
        getVehicleModels(vehicleModelFilter, {
            onSuccess: (response: ServiceResponse) => {
                setVehicleModels(response.data.vehicle_models);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2000
                });

                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (vehicleModelId: number) => {
        setShowingEdit(true);
        setVehicleModelIdEdit(vehicleModelId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setVehicleModelIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllVehicleModels();
    };

    const destroy = (vehicleModelId: number) => {
        const _text = 'Está a punto de eliminar el modelo #' + vehicleModelId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando modelo...');
                        deleteVehicleModel(vehicleModelId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El modelo de vehículo no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const active = (vehicleModelId: number, active: boolean) => {
        activeVehicleModel(vehicleModelId, active, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Modelos" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo modelo" />}
                />
                <VehicleModelDataTable
                    vehicleModels={vehicleModels}
                    edit={showEdit}
                    destroy={destroy}
                    active={active}
                    loading={fetchingGetVehicleModels}
                    filter={vehicleModelFilter}
                    setFilter={setVehicleModelFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Marca"
                    backdrop={true}
                    showFooter={false}
                >
                    <VehicleModelCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Modelo"
                    backdrop={true}
                    showFooter={false}
                >
                    <VehicleModelEdit
                        vehicleModelId={vehicleModelIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default VehicleModels;
